import { Box } from 'src/design/atoms/Box';
import { Collapse } from 'src/design/atoms/Collapse';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Text } from 'src/design/atoms/Text';
import { Transition } from 'src/design/atoms/Transition';
import {
  IconFilePlus,
  IconLinkPlus,
  IconPaperclip,
  IconX,
} from '@tabler/icons-react';
import React from 'react';
import {
  AttachmentDto,
  PostDocumentDto,
  PostLinkItemDto,
} from 'src/api/FiretrolApiClient';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { PostDocumentPicker } from './components/PostDocumentPicker';
import { PostFileUploader } from './components/PostFileUploader';
import { PostLinkPicker } from './components/PostLinkPicker';

interface PostActionsProps {
  onFileUploaded: (file: AttachmentDto) => void;
  onLinkSelected: (option: PostLinkItemDto) => void;
  onDocumentSelected: (doc: PostDocumentDto) => void;
  jobId: number;
}

export const PostActions: React.FC<PostActionsProps> = ({
  onFileUploaded,
  onLinkSelected,
  onDocumentSelected,
  jobId,
}) => {
  const [selectedAction, setSelectedAction] = React.useState<string | null>(
    null,
  );

  const onUpload = (file: AttachmentDto) => {
    onFileUploaded(file);
    setSelectedAction(null);
  };

  const onLinkAdded = (option: PostLinkItemDto) => {
    onLinkSelected(option);
    setSelectedAction(null);
  };

  const onDocumentAdded = (doc: PostDocumentDto) => {
    onDocumentSelected(doc);
    setSelectedAction(null);
  };

  return (
    <Paper withBorder p="xs">
      <Transition
        mounted={selectedAction === null}
        transition="fade"
        duration={200}
      >
        {(styles) => (
          <Group justify="space-between" style={styles}>
            <Text fw={500}>Add to your post</Text>
            <Group gap="xs">
              <BasicActionIcon
                label="Upload a file to attach to your post"
                variant="subtle"
                onClick={() => setSelectedAction('file')}
                size="sm"
              >
                <IconPaperclip size={16} />
              </BasicActionIcon>
              <BasicActionIcon
                label="Link a product from your job"
                variant="subtle"
                onClick={() => setSelectedAction('link')}
                size="sm"
              >
                <IconLinkPlus size={16} />
              </BasicActionIcon>
              <BasicActionIcon
                label="Link a document from your job"
                variant="subtle"
                onClick={() => setSelectedAction('document')}
                size="sm"
              >
                <IconFilePlus size={16} />
              </BasicActionIcon>
            </Group>
          </Group>
        )}
      </Transition>

      <Transition
        mounted={selectedAction !== null}
        transition="fade"
        duration={200}
      >
        {(styles) => (
          <Box style={styles}>
            <Group justify="flex-end">
              <BasicActionIcon
                variant="subtle"
                color="red"
                onClick={() => setSelectedAction(null)}
                size="sm"
              >
                <IconX size={16} />
              </BasicActionIcon>
            </Group>
          </Box>
        )}
      </Transition>

      <Collapse in={selectedAction === 'file'}>
        <PostFileUploader onFileUploaded={onUpload} />
      </Collapse>

      <Collapse in={selectedAction === 'link'}>
        <PostLinkPicker jobId={jobId} onLinkSelected={onLinkAdded} />
      </Collapse>

      <Collapse in={selectedAction === 'document'}>
        <PostDocumentPicker
          jobId={jobId}
          onDocumentSelected={onDocumentAdded}
        />
      </Collapse>
    </Paper>
  );
};
